import { Crown, ExternalLink, Target } from "lucide-react";
import { PlatformIcon } from "@/lib/platforms";

export default function HallOfFame({ entries }) {
  const latestKing = entries?.find((e) => (e.type || "king") === "king");
  const latestHunter = entries?.find((e) => e.type === "hunter");

  return (
    <section
      data-testid="hall-of-fame-section"
      className="relative rounded-3xl overflow-hidden border border-neon-gold/30"
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/8108685/pexels-photo-8108685.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "brightness(0.32) saturate(1.2)",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/60" aria-hidden />

      <div className="relative p-8 sm:p-10 lg:p-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-neon-gold mb-3">
            <Crown className="w-3.5 h-3.5" />
            HALL OF FAME
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-3">
            Yesterday's <span className="shine-text">Legends</span>
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            El King se lleva el 50% del pozo. El Hunter con más clicks únicos, el 10%.
            Hoy es una nueva batalla.
          </p>
        </div>

        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* KING CARD */}
          {latestKing ? (
            <a
              href={latestKing.profile_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hall-of-fame-king-link"
              className="block rounded-2xl bg-black/60 backdrop-blur border border-neon-gold/40 p-5 hover:border-neon-gold transition-colors"
            >
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-neon-gold mb-3">
                <Crown className="w-3 h-3" /> KING · 50%
              </div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-neon-gold/15 border border-neon-gold/40 flex items-center justify-center text-neon-gold">
                  <PlatformIcon platform={latestKing.platform_type} className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="font-heading font-bold text-base leading-none truncate">
                    {latestKing.handle_name}
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground mt-1">
                    {latestKing.date}
                  </div>
                </div>
              </div>
              <p className="text-xs text-white/80 italic line-clamp-2 mb-3">"{latestKing.custom_tagline}"</p>
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    Prize
                  </div>
                  <div className="font-mono text-2xl font-bold text-neon-gold">
                    ${Number(latestKing.prize_won_usd ?? 0).toFixed(2)}
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-neon-gold" />
              </div>
            </a>
          ) : (
            <div
              data-testid="hall-of-fame-empty"
              className="rounded-2xl bg-black/60 backdrop-blur border border-white/10 p-5"
            >
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-neon-gold mb-3">
                <Crown className="w-3 h-3" /> KING · 50%
              </div>
              <div className="font-heading font-bold text-base">
                Nadie ha reclamado el trono aún.
              </div>
            </div>
          )}

          {/* HUNTER CARD */}
          {latestHunter ? (
            <div
              data-testid="hall-of-fame-hunter"
              className="rounded-2xl bg-black/60 backdrop-blur border border-neon-red/40 p-5"
            >
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-neon-red mb-3">
                <Target className="w-3 h-3" /> HUNTER · 10%
              </div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-neon-red/15 border border-neon-red/40 flex items-center justify-center text-neon-red">
                  <Target className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="font-heading font-bold text-base leading-none truncate">
                    {latestHunter.email_masked}
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground mt-1">
                    {latestHunter.date}
                  </div>
                </div>
              </div>
              <p className="text-xs text-white/80 mb-3">
                {latestHunter.unique_clicks} clicks únicos · Pago a {latestHunter.payout_wallet_masked}
              </p>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  Prize
                </div>
                <div className="font-mono text-2xl font-bold text-neon-red">
                  ${Number(latestHunter.prize_won_usd ?? 0).toFixed(2)}
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl bg-black/60 backdrop-blur border border-white/10 p-5">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-neon-red mb-3">
                <Target className="w-3 h-3" /> HUNTER · 10%
              </div>
              <div className="font-heading font-bold text-base">
                Nadie ha cazado clicks aún.
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
